import React, { useState } from "react";
import { SubNav, FileDropzone, ImageCropper } from "../gc/ui";
import SpriteEditorPanel from "./SpriteEditorPanel";
import AnimationPanel from "./AnimationPanel";
import SpriteSearchPanel from "./SpriteSearchPanel";
import HudDesignerPanel from "./HudDesignerPanel";
import { projects as projectsApi } from "../../lib/api";
import { SPRITE_STUDIOS, readAsDataUrl, downloadDataUrl, recolorDataUrl, stitchAtlas } from "../../lib/gamecraft";
import { toast } from "sonner";

export default function SpriteHub({ project, onChange, onJump }) {
  const [studioId, setStudioId] = useState("creatures");
  const [sub, setSub] = useState("editor");
  const [typeFilter, setTypeFilter] = useState("all");
  const [cropSrc, setCropSrc] = useState(null);
  const [animImport, setAnimImport] = useState(null);
  const studio = SPRITE_STUDIOS.find((s) => s.id === studioId) || SPRITE_STUDIOS[0];
  const sprites = (project.sprites || []).filter((s) => (s.studio || "creatures") === studio.id);

  const sendToPlaytest = (s) => {
    const role = s.studio === "world" || /tile|piso|floor/i.test(s.name || s.category || "")
      ? "floorId"
      : s.studio === "gear" || /arma|sword|weapon/i.test(s.category || s.name || "")
        ? "weaponId"
        : "creatureId";
    onChange({ config: { ...(project.config || {}), playtest: { ...(project.config?.playtest || {}), [role]: s.id } } });
    onJump?.("engine");
    toast.success(`En el motor como ${role === "floorId" ? "piso" : role === "weaponId" ? "arma" : "criatura"}`);
  };

  const upload = async (files) => {
    for (const f of files) {
      const data_url = await readAsDataUrl(f);
      const payload = {
        name: f.name.replace(/\.[^.]+$/, ""),
        data_url,
        category: studio.kinds[0].id,
        subtype: (studio.subtypes || studio.kinds)[0]?.id,
        studio: studio.id,
      };
      try {
        await projectsApi.addSprite(project.project_id, payload);
      } catch {
        onChange({ sprites: [...(project.sprites || []), { id: crypto.randomUUID(), ...payload }] });
      }
    }
    const p = await projectsApi.get(project.project_id).catch(() => null);
    if (p) onChange({ sprites: p.sprites });
    toast.success("Sprite subido");
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {SPRITE_STUDIOS.map((s) => (
            <button
              key={s.id}
              onClick={() => { setStudioId(s.id); setSub("editor"); setTypeFilter("all"); }}
              className="px-3 py-2 rounded-2xl text-left border min-w-[160px]"
              style={{
                borderColor: studioId === s.id ? "var(--gc-primary)" : "var(--gc-border)",
                background: studioId === s.id ? "var(--gc-primary-soft)" : "var(--gc-surface)",
              }}
            >
              <div className="text-sm font-semibold">{s.label}</div>
              <div className="text-[11px] mt-0.5" style={{ color: "var(--gc-muted)" }}>{s.hint}</div>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SubNav
            items={[
              { id: "editor", label: "Editor" },
              { id: "search", label: "Buscar refs" },
              { id: "anim", label: "Animación" },
              { id: "library", label: "Biblioteca" },
            ]}
            value={sub}
            onChange={setSub}
          />
          <FileDropzone accept="image/*" multiple onFiles={upload} label="Subir imagen" />
          <button
            className="gc-chip"
            onClick={async () => {
              const url = await stitchAtlas(project.sprites || []);
              downloadDataUrl(url, `${project.slug || "game"}-atlas.png`);
              toast.success("Atlas exportado");
            }}
          >
            Export atlas
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        {studio.id === "hud" && sub === "editor" && (
          <div className="gc-card m-4">
            <HudDesignerPanel project={project} onChange={onChange} />
          </div>
        )}
        {studio.id !== "hud" && sub === "editor" && (
          <div className="gc-tool m-4">
            <SpriteEditorPanel project={project} onChange={onChange} studio={studio} />
          </div>
        )}
        {sub === "search" && (
          <SpriteSearchPanel
            project={project}
            onChange={onChange}
            studio={studio}
            onOpenAnim={(anim) => { setAnimImport(anim); setSub("anim"); }}
          />
        )}
        {sub === "anim" && (
          <div className="gc-tool m-4">
            <AnimationPanel project={project} onChange={onChange} importAnim={animImport} />
          </div>
        )}
        {sub === "library" && (
          <div className="p-4 space-y-3">
            <div className="flex flex-wrap gap-1">
              <button className={"gc-chip " + (typeFilter === "all" ? "gc-chip-on" : "")} onClick={() => setTypeFilter("all")}>Todos</button>
              {(studio.subtypes || studio.kinds || []).map((k) => (
                <button key={k.id} className={"gc-chip " + (typeFilter === k.id ? "gc-chip-on" : "")} onClick={() => setTypeFilter(k.id)}>
                  {k.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
            {sprites.filter((s) => typeFilter === "all" || s.subtype === typeFilter || s.category === typeFilter).map((s) => (
              <div key={s.id} className="gc-card p-2">
                <div className="aspect-square rounded-xl overflow-hidden flex items-center justify-center" style={{ background: "#0f172a" }}>
                  <img src={s.data_url} alt={s.name} className="max-w-full max-h-full" style={{ imageRendering: "pixelated" }} />
                </div>
                <div className="text-xs mt-2 truncate font-medium">{s.name}</div>
                <div className="text-[10px]" style={{ color: "var(--gc-muted)" }}>
                  {s.subtype || s.category}{s.role ? ` · ${s.role}` : ""}{(s.tags || []).length ? ` · ${(s.tags || []).slice(0, 2).join(", ")}` : ""}
                </div>
                <div className="flex gap-1 mt-1 flex-wrap">
                  <button className="gc-chip" onClick={() => downloadDataUrl(s.data_url, `${s.name}.png`)}>Descargar</button>
                  <button className="gc-chip" onClick={() => sendToPlaytest(s)}>Al motor</button>
                  <button className="gc-chip" onClick={async () => {
                    const url = await recolorDataUrl(s.data_url, 50);
                    await projectsApi.addSprite(project.project_id, { name: `${s.name}_var`, data_url: url, category: s.category, studio: s.studio }).catch(() => {});
                    const p = await projectsApi.get(project.project_id).catch(() => null);
                    if (p) onChange({ sprites: p.sprites });
                    toast.success("Variante recolor");
                  }}>Recolor</button>
                  <button className="gc-chip" onClick={() => setCropSrc(s.data_url)}>Recortar</button>
                </div>
              </div>
            ))}
            {sprites.filter((s) => typeFilter === "all" || s.subtype === typeFilter || s.category === typeFilter).length === 0 && (
              <div className="col-span-full text-sm p-8 text-center" style={{ color: "var(--gc-muted)" }}>
                Nada en {studio.label}. Dibuja, busca refs CC, genera con IA o sube una imagen.
              </div>
            )}
            </div>
          </div>
        )}
      </div>
      {cropSrc && (
        <ImageCropper
          src={cropSrc}
          onClose={() => setCropSrc(null)}
          onCrop={async (out) => {
            await projectsApi.addSprite(project.project_id, { name: "recorte", data_url: out, category: studio.kinds[0].id, studio: studio.id }).catch(() => {});
            const p = await projectsApi.get(project.project_id).catch(() => null);
            if (p) onChange({ sprites: p.sprites });
            setCropSrc(null);
            toast.success("Recorte guardado");
          }}
        />
      )}
    </div>
  );
}
